import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NotificationService } from '../../../data-access/notification.service';
import { renderTemplate, wrapHtmlForIframe } from '../../../data-access/template-renderer';
import { parseAddresses, serializeAddresses } from '../../../data-access/notification-addresses';
import {
	TemplateSummary,
	CurrentTemplate,
	TemplateVersion,
	SchemaVar,
	SaveTemplateRequest,
	TEMPLATE_VARIABLE_NAMES
} from '../../../domain/types/notification';
import { TemplateDiffModalComponent } from './template-diff-modal/template-diff-modal.component';

@Component({
	selector: 'notification-templates',
	templateUrl: './notification-templates.component.html',
	styleUrls: ['./notification-templates.component.scss']
})
export class NotificationTemplatesComponent implements OnInit {
	templates: TemplateSummary[] = [];
	listError = '';

	selectedName: string | null = null;
	/** The current server state of the loaded template (snapshot, not edited). */
	selected: CurrentTemplate | null = null;
	schema: SchemaVar[] = [];

	// Editable fields.
	subject = '';
	bodyHtml = '';
	ccList: string[] = [];
	bccList: string[] = [];
	ccInput = '';
	bccInput = '';
	changeNote = '';
	expectedRowVersion = '';

	/** Sample value per variable name, seeded from the schema, used for preview. */
	sampleValues: Record<string, string> = {};

	activeTab: 'edit' | 'history' = 'edit';
	history: TemplateVersion[] = [];

	// Live preview.
	renderedSubject = '';
	renderedBodySafe: SafeHtml = '' as unknown as SafeHtml;
	previewMissing: string[] = [];

	editorError = '';
	saveMessage = '';
	/** Set when a save was rejected (409); carries the latest server state. */
	conflictTemplate: CurrentTemplate | null = null;
	restoredFromVersion: number | null = null;
	lastSendMessage = '';

	constructor(
		private readonly svc: NotificationService,
		private readonly sanitizer: DomSanitizer,
		private readonly dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.loadTemplates();
	}

	async loadTemplates(): Promise<void> {
		this.listError = '';
		try {
			this.templates = await this.svc.listTemplates();
		} catch {
			this.listError = 'Failed to load templates.';
			this.templates = [];
		}
	}

	async selectTemplate(name: string): Promise<void> {
		this.selectedName = name;
		this.activeTab = 'edit';
		this.editorError = '';
		this.saveMessage = '';
		this.conflictTemplate = null;
		this.restoredFromVersion = null;
		this.lastSendMessage = '';
		try {
			const [template, schema] = await Promise.all([
				this.svc.getTemplate(name),
				this.svc.getTemplateSchema(name)
			]);
			this.schema = schema;
			this.sampleValues = {};
			schema.forEach(v => (this.sampleValues[v.name] = v.sample));
			this.applyTemplate(template);
		} catch {
			this.editorError = 'Failed to load the template.';
		}
	}

	/** Populate the editor's current server snapshot + editable fields. */
	private applyTemplate(t: CurrentTemplate): void {
		this.selected = t;
		this.expectedRowVersion = t.rowVersion;
		this.subject = t.subject;
		this.bodyHtml = t.bodyHtml;
		this.ccList = parseAddresses(t.defaultCcAddresses);
		this.bccList = parseAddresses(t.defaultBccAddresses);
		this.changeNote = '';
		this.updatePreview();
	}

	updatePreview(): void {
		const subjectResult = renderTemplate(this.subject, this.sampleValues);
		const bodyResult = renderTemplate(this.bodyHtml, this.sampleValues);
		this.renderedSubject = subjectResult.rendered;
		const previewDoc = wrapHtmlForIframe(bodyResult.rendered);
		this.renderedBodySafe = this.sanitizer.bypassSecurityTrustHtml(previewDoc);
		// Deduplicate across subject + body so the admin sees each unresolved name once.
		this.previewMissing = Array.from(new Set([...subjectResult.missingVars, ...bodyResult.missingVars]));
	}

	get previewTo(): string {
		return this.sampleValues[TEMPLATE_VARIABLE_NAMES.CandidateName] || 'Recipient';
	}

	/**
	 * The token to type for a variable. Raw-HTML variables (ready-made links) must
	 * use triple braces so the renderer emits them unencoded; everything else uses
	 * double braces (HTML-encoded). Showing the wrong token is why a {{TaskLink}}
	 * rendered as escaped text instead of a link.
	 */
	variableToken(v: SchemaVar): string {
		return v.html ? `{{{${v.name}}}}` : `{{${v.name}}}`;
	}

	// ── CC / BCC chips ──

	addCc(): void {
		const v = this.ccInput.trim();
		if (v && !this.ccList.includes(v)) this.ccList.push(v);
		this.ccInput = '';
	}

	removeCc(addr: string): void {
		this.ccList = this.ccList.filter(a => a !== addr);
	}

	addBcc(): void {
		const v = this.bccInput.trim();
		if (v && !this.bccList.includes(v)) this.bccList.push(v);
		this.bccInput = '';
	}

	removeBcc(addr: string): void {
		this.bccList = this.bccList.filter(a => a !== addr);
	}

	// ── Last real send example ──

	async loadLatestSend(): Promise<void> {
		if (!this.selectedName) return;
		this.lastSendMessage = '';
		try {
			const msg = await this.svc.getLatestMessageForTemplate(this.selectedName);
			if (!msg || !msg.templateDataJSON) {
				this.lastSendMessage = 'No sent messages found for this template yet.';
				return;
			}
			const data = JSON.parse(msg.templateDataJSON) as Record<string, string>;
			Object.keys(data).forEach(key => {
				if (key in this.sampleValues) this.sampleValues[key] = String(data[key]);
			});
			this.updatePreview();
			this.lastSendMessage = `Loaded sample values from the message sent ${msg.createdAt}.`;
		} catch {
			this.lastSendMessage = 'Failed to load the last real send.';
		}
	}

	// ── Save ──

	async save(): Promise<void> {
		if (!this.selectedName) return;
		this.editorError = '';
		this.saveMessage = '';
		const req: SaveTemplateRequest = {
			subject: this.subject,
			bodyHtml: this.bodyHtml,
			defaultCcAddresses: serializeAddresses(this.ccList),
			defaultBccAddresses: serializeAddresses(this.bccList),
			changeNote: this.changeNote.trim() || undefined,
			expectedRowVersion: this.expectedRowVersion
		};
		try {
			const result = await this.svc.saveTemplate(this.selectedName, req);
			if (result.conflict) {
				this.conflictTemplate = result.template;
				return;
			}
			this.applyTemplate(result.template);
			this.restoredFromVersion = null;
			this.saveMessage = `Saved version ${result.template.currentVersion}.`;
			this.refreshSummaryVersion(result.template);
		} catch {
			this.editorError = 'Failed to save the template.';
		}
	}

	/** Discard local edits and reload the latest server state after a conflict. */
	reloadConflict(): void {
		if (!this.conflictTemplate) return;
		this.applyTemplate(this.conflictTemplate);
		this.conflictTemplate = null;
		this.restoredFromVersion = null;
		this.saveMessage = 'Reloaded the latest version. Re-apply your changes and save.';
	}

	private refreshSummaryVersion(t: CurrentTemplate): void {
		const summary = this.templates.find(s => s.name === t.name);
		if (summary) summary.currentVersion = t.currentVersion;
	}

	// ── History ──

	async openHistory(): Promise<void> {
		this.activeTab = 'history';
		if (!this.selectedName) return;
		try {
			this.history = await this.svc.getTemplateHistory(this.selectedName);
		} catch {
			this.editorError = 'Failed to load version history.';
		}
	}

	viewDiff(version: TemplateVersion): void {
		if (!this.selected) return;
		TemplateDiffModalComponent.open(this.dialog, {
			versionNumber: version.versionNumber,
			oldSubject: version.subject,
			newSubject: this.selected.subject,
			oldBody: version.bodyHtml,
			newBody: this.selected.bodyHtml
		});
	}

	/** Load a historical version into the editor, unsaved, for review before saving. */
	restore(version: TemplateVersion): void {
		this.subject = version.subject;
		this.bodyHtml = version.bodyHtml;
		this.ccList = parseAddresses(version.defaultCcAddresses);
		this.bccList = parseAddresses(version.defaultBccAddresses);
		this.changeNote = `Restore of version ${version.versionNumber}`;
		this.restoredFromVersion = version.versionNumber;
		this.saveMessage = '';
		this.activeTab = 'edit';
		this.updatePreview();
	}
}
